import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Input, Modal, Radio, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { RETIRE_REASON_TYPES, itemNeedsHumanReview } from '../../utils/libraryTemplateModel';

const { Text } = Typography;

/**
 * Explicit human approval (Commandment IV): POST /admin/library-templates/:id/approve.
 * The API records the approver (`reviewedBy`/`reviewedAt`) from the caller's token and binds the
 * approval to the exact text the reviewer was shown: the request carries `bodyHash` (the hash of
 * the text on screen when they clicked Approve) and the API answers 409 if the body has changed
 * since. That 409 is handled as "review it again", never retried silently. For an AI-sourced item
 * the acknowledgement states what the automated reference check did or did not establish.
 */
export const LibraryApproveModal = ({ open, item, viewedBodyHash, citationSummary, onClose, onApproved, onStale }) => {
    const { request } = useApiRequest();
    const [acknowledged, setAcknowledged] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const ai = itemNeedsHumanReview(item);

    useEffect(() => {
        if (open) {
            setAcknowledged(false);
            setError(null);
        }
    }, [open]);

    const handleOk = async () => {
        setBusy(true);
        setError(null);
        try {
            const body = viewedBodyHash ? { bodyHash: viewedBodyHash } : undefined;
            const { data } = await request('POST', `/admin/library-templates/${item.id}/approve`, { body });
            message.success('Approved. It is not live until you publish it.');
            onApproved(data.item);
        } catch (err) {
            if (err.status === 409) {
                message.warning('This draft changed while you were reviewing - review it again.');
                onStale();
                return;
            }
            setError(err.message || 'Could not approve the template.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title={item ? `Approve ${item.controlRef} v${item.version}` : 'Approve'}
            okText="Approve"
            okButtonProps={{ disabled: !acknowledged }}
            confirmLoading={busy}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {ai && (
                    <Alert
                        type="warning"
                        showIcon
                        message="This text was drafted by AI"
                        description={
                            <div>
                                <div>{citationSummary ? citationSummary.text : 'No reference-check result is recorded for this draft.'}</div>
                                <div>No AI agreement score is computed for library drafts - the only automated check is the reference check above, so you are the check on the content.</div>
                            </div>
                        }
                    />
                )}
                <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
                    {ai
                        ? 'I have read the full AI-drafted text and checked it against the standard.'
                        : 'I have read the full text and confirm it is ready to publish.'}
                </Checkbox>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Your approval is bound to the text you are looking at{viewedBodyHash ? ` (fingerprint ${viewedBodyHash.slice(0, 8)})` : ''} and is recorded in the audit log with your identity and the time. If the text changes first, the approval is refused.
                </Text>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

/**
 * Retire a published template (POST .../retire): a reason and a reason type are required.
 * "Correction" means the template was factually wrong — every project that used this version is
 * actively notified (Commandment IX: confirm before a consequential, hard-to-reverse action).
 */
export const LibraryRetireModal = ({ open, item, onClose, onRetired }) => {
    const { request } = useApiRequest();
    const [reasonType, setReasonType] = useState('routine');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setReasonType('routine');
            setReason('');
            setError(null);
        }
    }, [open]);

    const handleOk = async () => {
        if (!reason.trim()) return setError('State why this template is being retired.');
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('POST', `/admin/library-templates/${item.id}/retire`, {
                body: { retiredReason: reason.trim(), retiredReasonType: reasonType },
            });
            const notified = (data.notifiedProjectIds || []).length;
            const fanOut = data.notificationFanOut;
            const failedCount = (fanOut?.failedProjectIds || []).length;
            if (reasonType === 'correction' && fanOut && fanOut.status !== 'ok') {
                message.warning(
                    `Retired, but ${failedCount} project(s) that used it could NOT be notified (${notified} were). Follow up with them directly; the failures are in the audit log.`,
                );
            } else {
                message.success(
                    reasonType === 'correction'
                        ? `Retired. ${notified} project(s) that used it have been notified.`
                        : 'Retired.',
                );
            }
            onRetired(data.item);
        } catch (err) {
            setError(err.message || 'Could not retire the template.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title={item ? `Retire ${item.controlRef} v${item.version}` : 'Retire'}
            okText="Retire template"
            okButtonProps={{ danger: true }}
            confirmLoading={busy}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Alert
                    type="warning"
                    showIcon
                    message="Retiring removes this version from what projects receive"
                    description="Drafts already generated from it keep the version they used. To replace it, fork a new version and publish that."
                />
                <Radio.Group value={reasonType} onChange={(e) => setReasonType(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {RETIRE_REASON_TYPES.map((t) => (
                        <Radio key={t.value} value={t.value}>{t.label}</Radio>
                    ))}
                </Radio.Group>
                {reasonType === 'correction' && (
                    <Alert type="error" showIcon message="Every project that used this version will be notified that it was wrong." />
                )}
                <div>
                    <Text strong>Reason</Text>
                    <Input.TextArea aria-label="Retirement reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};
